/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  retry,
  Subscription,
  tap,
} from 'rxjs';

import {
  IBillingType,
  IProviderDetail,
} from '../../interfaces/provider/provider.interfaces';
import { AlertService } from '../../services/alert.service';
import { ProviderGQLService } from '../../services/provider/provider.graphql.service';
import { ProviderService } from '../../services/provider/provider.service';
import { SharedDataService } from '../../services/shared/shared-data.service';

@Component({
  selector: 'qualifiers-grid',
  templateUrl: './qualifiers-grid.component.html',
})
export class QualifiersGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() groupId?: string;
  @Input() providerId: string;

  @Input() isProviderGroup: boolean;
  @Input() isProvider: boolean;

  @Input() isReadOnly = false;

  detailsForm: UntypedFormGroup;

  isProviderDetailEdit: boolean;
  isLoading: boolean;
  isInitialQualifiers = false;

  providerDetailsGrid: IProviderDetailGrid[] = [];

  private currentProviderDetailId?: string;
  private subscriptions: Subscription = new Subscription();

  idValue = '';

  constructor(
    private fb: UntypedFormBuilder,
    private providerService: ProviderService,
    private alertService: AlertService,
    private providerGQLService: ProviderGQLService,
    private sharedDataService: SharedDataService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.groupId.currentValue || changes.providerId.currentValue) {
      const arg =
        this.providerId && this.isProvider
          ? { providerId: this.providerId }
          : this.groupId && this.isProviderGroup
            ? { providerGroupId: this.groupId }
            : null;

      if (arg) {
        const request$ = this.providerGQLService.getProviderBillings(arg, 'A');

        this.subscriptions.add(
          request$
            .pipe(
              tap(() => {
                this.isLoading = true;
              }),
            )
            .subscribe({
              next: (x) => {
                this.providerDetailsGrid = x.nodes.map((b, index) => {
                  const d: Partial<IProviderDetailGrid> = {
                    id: b.billingId,
                    oldId: b.billingId,
                    qualifier: b.billingIdTypeCode,
                    index,
                    rowId: b.rowId,
                    status: b.status,
                    description: b.billingIdType?.description,
                    groupId: b.providerGroupId,
                    saved: true,
                    error: false,
                    editing: false,
                  };

                  return d;
                }) as IProviderDetailGrid[];

                this.isInitialQualifiers = true;

                this.isLoading = false;
              },
              error: (error) => {
                console.log(error);
                this.isLoading = false;
              },
            }),
        );
      }
    }
  }

  ngOnInit() {
    this.detailsForm = this.fb.group({
      qualifier: [null, Validators.required],
      id: [
        null,
        [
          Validators.required,
          this.validateIdLength.bind(this),
          this.uniqueProviderDetailsId(undefined),
        ],
      ],
    });

    if (this.isReadOnly) {
      this.detailsForm.disable();
    }
  }

  onIdChange(value: string) {
    const trimmedValue = value.trim();
    if (this.idValue === trimmedValue) {
      return;
    }
    this.idValue = trimmedValue;
    const detailsFormId = this.detailsForm.get('id');

    if (detailsFormId) {
      detailsFormId.setValue(trimmedValue);
    }
  }

  validateIdLength(control: AbstractControl): null | Record<string, any> {
    const formGroup = control.root as FormGroup;
    const qualifierValue = formGroup.get('qualifier')?.value;
    const qualifierValueCode = qualifierValue?.code || null;
    const idValue = formGroup.get('id')?.value;

    if (qualifierValueCode === 'XX' && idValue.length !== 10) {
      return { invalidIdLength: 'NPI must be exactly 10 characters long.' };
    } else if (qualifierValueCode === 'EI' && idValue.length !== 9) {
      return { invalidIdLength: 'TAX ID must be exactly 9 characters long.' };
    } else {
      return null;
    }
  }

  addProviderDetails() {
    const detailsFormValue = this.detailsForm.getRawValue();
    const selectedQualifier = detailsFormValue.qualifier;

    const idValue = detailsFormValue.id;
    const qualifierCode = selectedQualifier?.code || null;

    const providerDetail: IProviderDetailGrid = {
      ...detailsFormValue,
      qualifier: selectedQualifier.code,
      description: selectedQualifier.description,
      groupId: this.groupId,
      editing: false,
      saved: false,
      error: false,
    };

    const index = this.providerDetailsGrid.findIndex((x) => {
      return x.id == this.currentProviderDetailId;
    });

    if (
      (qualifierCode == 'XX' && idValue.length != 10) ||
      (qualifierCode == 'EI' && idValue.length != 9)
    ) {
      this.detailsForm.reset();
      return;
    }
    if (index >= 0) {
      this.providerDetailsGrid[index] = {
        ...this.providerDetailsGrid[index],
        ...providerDetail,
      };
    } else {
      this.providerDetailsGrid.push(providerDetail);
    }

    this.detailsForm.reset();

    this.isProviderDetailEdit = false;
    this.currentProviderDetailId = undefined;
  }

  editProviderDetails(detail: IProviderDetailGrid) {
    detail.editing = true;
    detail.saved = false;

    this.isProviderDetailEdit = true;
    this.currentProviderDetailId = detail.id;

    const controlId = this.detailsForm.get('id');

    controlId?.setValidators([
      Validators.required,
      this.uniqueProviderDetailsId(detail.id),
    ]);

    this.detailsForm.patchValue({
      qualifier: detail.qualifier,
      id: detail.id,
    });

    this.detailsForm.markAsUntouched();
  }

  deleteProviderDetails(detail: IProviderDetailGrid) {
    this.providerDetailsGrid = this.providerDetailsGrid.filter(
      (providerDetail) => {
        return providerDetail.id !== detail.id;
      },
    );

    this.detailsForm.reset();
  }

  getMaxLength(): number {
    return this.sharedDataService.maxLength;
  }

  uniqueProviderDetailsId(currentValue?: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value == undefined) {
        return null;
      }

      const value = control.value as string;

      if (value == currentValue) {
        return null;
      }

      const isValid = this.providerDetailsGrid.every((x) => {
        return x.id != value;
      });

      return isValid
        ? null
        : {
            uniqueProviderDetailsId: {
              valid: false,
            },
          };
    };
  }

  saveQualifiers(
    providerId?: string,
    providerGroupId?: string,
  ): Observable<boolean> {
    if (providerGroupId) {
      this.groupId = providerGroupId;
    }

    this.providerDetailsGrid = this.providerDetailsGrid.map((pd, index) => {
      pd.index = index;
      return pd;
    });

    const qualifiersToSave = this.providerDetailsGrid.filter((x) => {
      return !x.saved;
    });

    const qualifiers$ = qualifiersToSave.map((pd) => {
      const args = pd.rowId
        ? {
            billingId: pd.id,
            billingIdTypeCode: pd.qualifier,
            providerId: providerId ?? undefined,
            providerGroupId: this.groupId,
            rowId: pd.rowId,
            status: pd.status || 'A',
          }
        : {
            providerGroupId: this.groupId,
            billingId: pd.id,
            billingIdTypeCode: pd.qualifier,
            status: 'A',
            providerId: providerId ?? undefined,
          };

      const request$ = pd.rowId
        ? this.providerService.editBillingType(args, pd.rowId)
        : this.providerService.addBillingType(args);

      const qualifier = this.providerDetailsGrid.find((x) => {
        return x.id === pd.id;
      });

      return request$.pipe(
        tap(() => {
          if (qualifier) {
            return (qualifier.saved = true);
          }

          return undefined;
        }),
        map((value) => {
          return value;
        }),
        retry(1),
        catchError((error) => {
          if (qualifier) {
            qualifier.saved = false;
            qualifier.error = true;
          }

          this.isInitialQualifiers = false;

          const title = `Unable to save qualifier with id: "${pd.id}"`;
          const message = error?.error || error?.message || error;

          this.alertService.warning(title, message as string, true);
          return of(false);
        }),
      );
    });

    return qualifiers$.length > 0
      ? combineLatest(qualifiers$).pipe(
          map((arr) => {
            return arr.every((x) => {
              return !!x;
            });
          }),
        )
      : of(true);
  }

  getQualifiersData(): IBillingType[] {
    return this.providerDetailsGrid.map((pd) => {
      return pd.rowId
        ? {
            rowId: pd.rowId,
            billingId: pd.id,
            billingIdTypeCode: pd.qualifier,
            status: pd.status || 'A',
            saved: pd.saved,
            error: pd.error,
          }
        : {
            billingId: pd.id,
            billingIdTypeCode: pd.qualifier,
            status: 'A',
            saved: pd.saved,
            error: pd.error,
          };
    });
  }
}

type IProviderDetailGrid = {
  saved: boolean;
  error: boolean;
  editing: boolean;
} & IProviderDetail;
